import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Receipt, RefreshCw, ExternalLink } from "lucide-react";

interface UnbilledLead {
  id: string;
  buyer_name: string | null;
  buyer_email: string | null;
  created_at: string;
  listing_id: string;
  listing_title: string;
  listing_number: string | null;
  account_user_id: string;
  account_email: string;
  account_name: string | null;
}

interface AccountGroup {
  account_user_id: string;
  account_email: string;
  account_name: string | null;
  leads: UnbilledLead[];
}

interface PricingConfig {
  lead: { fee_display: string; fee_cents: number };
}

export function AdminLeadBilling() {
  const { toast } = useToast();
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState<string | null>(null);
  const [pricing, setPricing] = useState<PricingConfig>({ lead: { fee_display: "AUD $19", fee_cents: 1900 } });

  const load = async () => {
    setLoading(true);
    try {
      const { data: settings } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "pricing_config")
        .maybeSingle();
      if (settings?.value) setPricing(settings.value as unknown as PricingConfig);

      // Load unbilled leads + listings (admin policy allows this)
      const { data: leads, error } = await supabase
        .from("leads")
        .select("id, buyer_name, buyer_email, created_at, listing_id, listings:listing_id(title, listing_number, broker_id)")
        .eq("billed", false)
        .order("created_at", { ascending: false });
      if (error) throw error;

      // Resolve account profile info for each unique broker_id
      const brokerIds = Array.from(new Set((leads ?? []).map((l: any) => l.listings?.broker_id).filter(Boolean)));
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", brokerIds.length ? brokerIds : ["00000000-0000-0000-0000-000000000000"]);
      const profileMap = new Map((profiles ?? []).map((p: any) => [p.user_id, p]));

      // We don't expose auth.users to client; show user_id as fallback identifier
      const enriched: UnbilledLead[] = (leads ?? [])
        .filter((l: any) => l.listings?.broker_id)
        .map((l: any) => ({
          id: l.id,
          buyer_name: l.buyer_name,
          buyer_email: l.buyer_email,
          created_at: l.created_at,
          listing_id: l.listing_id,
          listing_title: l.listings?.title ?? "Untitled",
          listing_number: l.listings?.listing_number ?? null,
          account_user_id: l.listings.broker_id,
          account_email: profileMap.get(l.listings.broker_id)?.full_name ?? l.listings.broker_id,
          account_name: profileMap.get(l.listings.broker_id)?.full_name ?? null,
        }));

      // Group by account
      const byAccount = new Map<string, AccountGroup>();
      for (const lead of enriched) {
        const g = byAccount.get(lead.account_user_id) ?? {
          account_user_id: lead.account_user_id,
          account_email: lead.account_email,
          account_name: lead.account_name,
          leads: [],
        };
        g.leads.push(lead);
        byAccount.set(lead.account_user_id, g);
      }
      setGroups(Array.from(byAccount.values()));
    } catch (e) {
      console.error(e);
      toast({ title: "Error", description: "Failed to load unbilled leads", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleLead = (id: string) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleGroup = (g: AccountGroup, checked: boolean) =>
    setSelected((s) => {
      const next = { ...s };
      g.leads.forEach((l) => (next[l.id] = checked));
      return next;
    });

  const billGroup = async (g: AccountGroup) => {
    const leadIds = g.leads.filter((l) => selected[l.id]).map((l) => l.id);
    if (leadIds.length === 0) {
      toast({ title: "No leads selected", description: "Select at least one lead to bill." });
      return;
    }
    if (!confirm(`Generate Stripe invoice for ${leadIds.length} lead(s) — total ${pricing.lead.fee_display} × ${leadIds.length}?`)) return;
    setBilling(g.account_user_id);
    try {
      const { data, error } = await supabase.functions.invoke("admin-bill-leads", {
        body: { account_user_id: g.account_user_id, lead_ids: leadIds },
      });
      if (error) throw error;
      toast({
        title: "Invoice created",
        description: `${data.leads_billed} lead(s) billed. Stripe invoice ${data.invoice_id} sent.`,
      });
      if (data.invoice_url) window.open(data.invoice_url, "_blank");
      await load();
      setSelected({});
    } catch (e: any) {
      console.error(e);
      toast({ title: "Billing failed", description: e?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setBilling(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Lead Billing
          </CardTitle>
          <CardDescription>
            Charge unbilled buyer leads at {pricing.lead.fee_display} each via Stripe invoice.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">🎉 No unbilled leads.</p>
        ) : (
          <div className="space-y-6">
            {groups.map((g) => {
              const allChecked = g.leads.every((l) => selected[l.id]);
              const someChecked = g.leads.some((l) => selected[l.id]);
              const selectedCount = g.leads.filter((l) => selected[l.id]).length;
              return (
                <div key={g.account_user_id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="font-semibold">{g.account_name ?? g.account_user_id}</p>
                      <p className="text-xs text-muted-foreground font-mono">{g.account_user_id}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{g.leads.length} unbilled</Badge>
                      <Button
                        size="sm"
                        onClick={() => billGroup(g)}
                        disabled={billing === g.account_user_id || selectedCount === 0}
                      >
                        {billing === g.account_user_id ? "Billing…" : `Bill ${selectedCount || ""} via Stripe`}
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={allChecked}
                      onCheckedChange={(v) => toggleGroup(g, !!v)}
                      aria-label="Select all"
                    />
                    <span className="text-muted-foreground">Select all in this account</span>
                  </div>
                  <div className="divide-y">
                    {g.leads.map((l) => (
                      <div key={l.id} className="flex items-center gap-3 py-2 text-sm">
                        <Checkbox checked={!!selected[l.id]} onCheckedChange={() => toggleLead(l.id)} />
                        <div className="flex-1">
                          <p>
                            <strong>{l.buyer_name ?? "(no name)"}</strong> — {l.buyer_email ?? "(no email)"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {l.listing_title}
                            {l.listing_number ? ` · #${l.listing_number}` : ""} ·{" "}
                            {new Date(l.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
